// Las opciones de una pregunta de Cumbre (ADR-2026-10-02-onboarding-v2-preguntas-con-cumbre):
// tarjetas grandes con ícono, que entran en cascada cuando Cumbre termina de preguntar. Se
// puede marcar más de una (ADR-2026-10-02-onboarding-v2-preguntas-de-varias) y "Listo"
// confirma; con mouse y teclado, las letras (A, B, C…) marcan y Enter confirma. "Otro" abre
// un campo para escribir la respuesta (ADR-2026-09-29-onboarding-v2-preguntas-con-otro).

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { RESPUESTA_OTRO } from '../../../lib/onboardingMarca/marca';
import { alternarOpcion, LETRAS_DE_OPCION, MAX_OTRO, type Pregunta } from '../../../lib/onboardingMarca/preguntas';

function esCampoDeTexto(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  return Boolean(el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable));
}

export function OpcionesDePregunta({
  pregunta,
  confirmadas,
  sugerida,
  onConfirmar,
  onEscribiendo,
}: {
  pregunta: Pregunta;
  /** Lo que ya confirmó, mientras Cumbre reacciona: lo demás se apaga. */
  confirmadas: readonly string[] | null;
  /** La opción que sugiere el link (sólo en la del canal). */
  sugerida: string | null;
  /** Todo lo marcado, en el orden en que se marcó (la principal primero), y el texto de "Otro". */
  onConfirmar: (ids: string[], textoOtro: string) => void;
  /** Para que Cumbre mire el campo de "Otro" mientras se escribe. */
  onEscribiendo: (si: boolean) => void;
}) {
  const [marcadas, setMarcadas] = useState<string[]>([]);
  const [texto, setTexto] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const letraOtro = LETRAS_DE_OPCION[pregunta.opciones.length];
  const cerrado = confirmadas !== null;
  const conOtro = marcadas.includes(RESPUESTA_OTRO);
  const listo = marcadas.length > 0 && (!conOtro || Boolean(texto.trim()));

  const alternar = (id: string) => {
    if (cerrado) return;
    // "Otro" pasa por la misma regla que el resto: marcarlo apaga "Ninguno" o "Todavía no"
    // (antes se sumaba directo y quedaban las dos; review Greptile #164). Al abrirse, el foco
    // va a su campo.
    if (id === RESPUESTA_OTRO && !conOtro) window.setTimeout(() => inputRef.current?.focus(), 0);
    setMarcadas((m) => alternarOpcion(m, id, pregunta));
  };

  const confirmar = (e?: FormEvent) => {
    e?.preventDefault();
    if (cerrado || !listo) return;
    onConfirmar(marcadas, conOtro ? texto.trim() : '');
  };

  useEffect(() => {
    if (cerrado) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (esCampoDeTexto(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        confirmar();
        return;
      }
      const letra = e.key.toUpperCase();
      if (letra === letraOtro) {
        e.preventDefault();
        alternar(RESPUESTA_OTRO);
        return;
      }
      const opcion = pregunta.opciones[LETRAS_DE_OPCION.indexOf(letra)];
      if (LETRAS_DE_OPCION.includes(letra) && opcion) {
        e.preventDefault();
        alternar(opcion.id);
      }
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  });

  const activa = (id: string) => (confirmadas ? confirmadas.includes(id) : marcadas.includes(id));
  const claseDe = (id: string) => `om-opcion ${activa(id) ? 'is-elegida' : ''} ${cerrado && !activa(id) ? 'is-apagada' : ''}`;

  return (
    <div className="mt-6 flex flex-col gap-3">
      <p className="om-varias">Puedes marcar más de una.</p>
      <div className="om-opciones om-aparece" role="group" aria-label={pregunta.titulo}>
        {pregunta.opciones.map((o, i) => (
          <button
            key={o.id}
            type="button"
            role="checkbox"
            aria-checked={activa(o.id)}
            disabled={cerrado && !activa(o.id)}
            onClick={() => alternar(o.id)}
            className={claseDe(o.id)}
          >
            <span className="om-opcion__icono material-symbols-rounded" aria-hidden="true">
              {o.icono}
            </span>
            <span className="min-w-0 flex-1">
              {o.label}
              {o.id === sugerida && !activa(o.id) && <span className="om-opcion__sugerida">por tu link</span>}
            </span>
            <OpcionFin letra={LETRAS_DE_OPCION[i]} activa={activa(o.id)} />
          </button>
        ))}
        <button
          type="button"
          role="checkbox"
          aria-checked={activa(RESPUESTA_OTRO)}
          aria-expanded={conOtro}
          disabled={cerrado && !activa(RESPUESTA_OTRO)}
          onClick={() => alternar(RESPUESTA_OTRO)}
          className={claseDe(RESPUESTA_OTRO)}
        >
          <span className="om-opcion__icono material-symbols-rounded" aria-hidden="true">
            edit
          </span>
          <span className="min-w-0 flex-1">Otro</span>
          <OpcionFin letra={letraOtro} activa={activa(RESPUESTA_OTRO)} />
        </button>
      </div>

      <form onSubmit={confirmar} className="flex flex-col gap-3">
        {conOtro && (
          <div className="om-otro om-llega">
            <label htmlFor={`om-otro-${pregunta.clave}`} className="sr-only">
              {pregunta.titulo} Otro
            </label>
            <input
              id={`om-otro-${pregunta.clave}`}
              ref={inputRef}
              value={texto}
              maxLength={MAX_OTRO}
              placeholder={pregunta.ejemploOtro}
              disabled={cerrado}
              onChange={(e) => setTexto(e.target.value)}
              onFocus={() => onEscribiendo(true)}
              onBlur={() => onEscribiendo(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setMarcadas((m) => m.filter((x) => x !== RESPUESTA_OTRO));
                  setTexto('');
                }
              }}
              className="min-w-0 flex-1 bg-transparent text-[16px] font-semibold text-brand-black outline-none placeholder:font-normal placeholder:text-[#98A1C0]"
            />
          </div>
        )}
        {/* Fijo abajo y siempre a la vista: con muchas opciones (o el campo de "Otro") el botón
            quedaba debajo del borde y no se veía que había que confirmar. */}
        {!cerrado && (
          <div className="om-barra-fija">
            <div className="om-barra__dentro">
              <button type="submit" disabled={!listo} className="om-boton om-aparece-solo md:ml-auto md:w-auto md:px-12">
                Listo
                <kbd className="hidden rounded bg-white/15 px-1.5 text-[11px] md:inline">⏎</kbd>
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

/**
 * A la derecha: el casillero (en el teléfono) o la letra del atajo (con mouse y teclado; el
 * CSS elige cuál se ve), y el tilde cuando está marcada.
 */
function OpcionFin({ letra, activa }: { letra: string; activa: boolean }) {
  if (activa) {
    return (
      <span className="om-opcion__tilde" aria-hidden="true">
        <svg viewBox="0 0 20 20" className="h-3.5 w-3.5">
          <path d="M4.5 10.5l3.5 3.5 7.5-8" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  return (
    <>
      <span className="om-opcion__radio om-opcion__radio--varias" aria-hidden="true" />
      <kbd className="om-opcion__letra">{letra}</kbd>
    </>
  );
}

export default OpcionesDePregunta;
